import type { TLocalize } from '@hooks/use-localize';
import type { ILabOrdersModel } from '@/types/history/lab-orders';
import { Link } from 'react-router';

interface IProps {
    model: ILabOrdersModel;
    t: TLocalize;
}

const QUEST_APPOINTMENT_URL = 'https://secure.questdiagnostics.com/hcp/psc/jsp/SearchLocation.do';

/** The scrip is ready and the donor is visiting a Quest location in person. */
const ReadyQuestSection = ({ model, t }: IProps) => {
    const questAppointmentStep = (
        <>
            {t('DashLabel_ScheduleAnAppointmentWithQuest', 'Schedule an appointment with Quest')} (
            <a href={QUEST_APPOINTMENT_URL} target="_blank" rel="noopener noreferrer">
                {t('DashLabel_QuestAppointmentPage', 'Quest Appointment Page')}
            </a>
            ) {t('DashLabel_AndSelectAllOtherTestsAsTheReasonForVisiting', 'and select "All Other Tests" as the reason for visiting')}.
        </>
    );

    return (
        <div className="row row-margined">
            <div className="col-xs-12">
                <h1>{t('DashLabel_YouReReadyForLabs', "You're Ready for Labs")}.</h1>

                <p>
                    {t('DashLabel_QuestAlreadyHasTheLabOrdersUnderTheName', 'Quest already has the lab orders under the name')}{' '}
                    {model.fullName}, {t('DashLabel_AndBirthdate', 'and birthdate')},{' '}
                    {model.dateOfBirth}.{' '}
                    {t('DashLabel_IfYouWishYouCanAlsoDownloadAndPrintTheOrdersToTakeWithYou', 'If you wish, you can also download and print the orders to take with you by')}{' '}
                    <a
                        href={'/api/register/pwl-download-scrip/' + model.id + '/' + model.editKey}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('DashLabel_ClickingHere', 'clicking here')}
                    </a>
                </p>

                <h4>{t('DashLabel_NextSteps', 'Next Steps')}</h4>

                {model.hasJugStatus ? (
                    <ol>
                        <li>
                            {t('DashLabel_WaitForYourUrineCollectionJugToArrive', 'Wait for your urine collection jug to arrive')}.
                        </li>
                        <li>{questAppointmentStep}</li>
                        <li>
                            {t('DashLabel_StartYourUrineCollectionApproximately24HoursPriorToYourAppointment', 'Start your urine collection approximately 24 hours prior to your appointment')}{' '}
                            (
                            <a
                                href="/register/pwl-jug-instructions"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                instructions
                            </a>
                            )
                        </li>
                        <li>
                            {t('DashLabel_TakeYourFilledUrineJugAndLabOrdersToQuestAtTheTimeOfYour', 'Take your filled urine jug and lab orders to Quest at the time of your scheduled appointment. At the lab, they will also draw your blood and you will need to provide a fresh urine sample. Fasting is not necessary')}.
                        </li>
                    </ol>
                ) : (
                    <ol>
                        <li>{questAppointmentStep}</li>
                        <li>
                            {t('DashLabel_AtYourAppointmentYouWillHaveYourBloodDrawnAndSubmitAUrine', 'At your appointment, you will have your blood drawn and submit a urine sample.')}.
                        </li>
                    </ol>
                )}

                {model.hasPreferredCenter && (
                    <p>
                        {t('DashLabel_NotePreferredCenterContactPostPwl', 'You should be contacted by your coordinator at your previously selected Preferred Center within a few days after your appointment')}
                    </p>
                )}

                {model.isNextStepCenterSelect ? (
                    <p>
                        {t('DashLabel_YouShouldReceiveAnEmailAppoint', 'You should receive an e-mail with instructions to select your preferred donation center within a few days after your appointment.')}
                    </p>
                ) : (
                    <p>
                        {t('DashLabel_IfYouDoNotHearBackFromTheTransplantCenterWithin2WeeksOfCo', 'If you do not hear back from the transplant center within 2 weeks of completing the lab results, please call them')}
                        {model.plDashPhoneNumber
                            ? t('DashLabel_WordAt', ' at ') + model.plDashPhoneNumber
                            : ''}
                        .
                    </p>
                )}

                <div className="journey-opt-out">
                    You may withdraw at any time using this link: <Link to={'/register/opt-out/' + model.id + '/' + model.editKey}>Opt-out of the donation process</Link>
                </div>
            </div>
        </div>
    );
};

export default ReadyQuestSection;
